import type { ReactNode } from 'react';
import { Accordion, Alert, Badge, Button, DefinitionList, Inline, Link, ProgressBar, SplitLayout, Stack, t, Text, Timeline } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { durationSince, relativeTime } from '../../lib/format';

type Installation = maintenance.components['schemas']['Installation'];

/** SPEC-034 § 6.2: the steps of an installation, in order, and the states each one ends with. */
export const PROGRESS = [
  { step: 'reserve', done: ['reserved', 'preparing', 'prepared', 'catalogued', 'ready', 'deploying', 'deployed', 'in_service'] },
  { step: 'prepare', done: ['prepared', 'catalogued', 'ready', 'deploying', 'deployed', 'in_service'] },
  { step: 'catalogue', done: ['catalogued', 'ready', 'deploying', 'deployed', 'in_service'] },
  { step: 'read', done: ['ready', 'deploying', 'deployed', 'in_service'] },
  // § 6.2 step 5: the required secrets set in the GitHub environment (done when its step is recorded).
  { step: 'secrets', done: ['deploying', 'deployed', 'in_service'] },
  { step: 'deploy', done: ['deployed', 'in_service'] },
  { step: 'service', done: ['in_service'] },
] as const;

/** A failed installation keeps the steps it went through: those before the failed step are done. */
const failedAt = (i: Installation) => (i.state === 'failed' && i.failedStep ? PROGRESS.findIndex(p => p.step === i.failedStep) : -1);
const doneStep = (i: Installation, p: (typeof PROGRESS)[number]) => (p.done as readonly string[]).includes(i.state)
  || (p.step === 'secrets' && Boolean(i.steps.secrets))
  || PROGRESS.indexOf(p) < failedAt(i);

/** The step an installation is at (the first not done), and since when (the end of the one before, else its start). */
export function currentStep(i: Installation): { step: string; since: string | null } | null {
  if (['in_service', 'abandoned'].includes(i.state) || kindOf(i) === 'retired') return null;
  const at = i.state === 'failed' && i.failedStep ? PROGRESS.findIndex(p => p.step === i.failedStep) : PROGRESS.findIndex(p => !doneStep(i, p));
  if (at < 0) return null;
  const before = at > 0 ? i.steps[PROGRESS[at - 1]!.step] : null;
  return { step: PROGRESS[at]!.step, since: before ?? i.steps.started ?? i.createdAt };
}

/** One installation's seven steps (DSM Timeline). */
export function progressItems(i: Installation, failedActions?: ReactNode) {
  const current = currentStep(i);
  return PROGRESS.map(p => {
    const done = doneStep(i, p);
    const failedHere = i.state === 'failed' && i.failedStep === p.step;
    const isCurrent = !done && !failedHere && i.state !== 'failed' && !i.waitingFor && current?.step === p.step;
    return {
      id: p.step,
      title: t(`install.progress.${p.step}`),
      status: (done ? 'done' : failedHere ? 'error' : isCurrent ? 'current' : 'upcoming') as 'done' | 'error' | 'current' | 'upcoming',
      when: i.steps[p.step] ? relativeTime(i.steps[p.step]!) : undefined,
      description: failedHere ? (failedActions ? <Stack gap={8}><span>{i.failure}</span>{failedActions}</Stack> : i.failure ?? undefined)
        : p.step === 'deploy' && i.state === 'deploying' && i.steps.dispatch ? t('install.progress.deployWorkflow')
        : t(`install.progress.${p.step}Help`),
    };
  });
}

type Kind = 'done' | 'running' | 'waiting' | 'failed' | 'abandoned' | 'variables' | 'retired';
const RETIRE_STEPS = ['retire', 'unlink', 'purge', 'reset'];
const kindOf = (i: Installation): Kind => (i.state === 'in_service' ? 'done' : i.state === 'abandoned' ? 'abandoned'
  // § 12.4: an application of the batch retired since; its page follows its retirement.
  : ['retire_requested', 'retiring', 'unlinking', 'retired', 'purging', 'resetting', 'purged'].includes(i.state) || RETIRE_STEPS.includes(i.failedStep ?? '') ? 'retired'
  : i.state === 'failed' ? 'failed'
  : i.waitingFor ? 'waiting' : i.state === 'ready' && !i.steps.secrets ? 'variables' : 'running');
const TONES: Record<Kind, 'pos' | 'neg' | 'neutral' | 'brand' | 'warn' | 'info'> = { done: 'pos', running: 'brand', waiting: 'neutral', failed: 'neg', abandoned: 'neutral', variables: 'warn', retired: 'neutral' };
const resumes = (i: Installation) => Object.keys(i.steps).filter(k => k.startsWith('resume_')).length;
/** When the installation was last taken up again, null when never. */
const lastResume = (i: Installation) => (resumes(i) ? i.steps[`resume_${resumes(i)}`] ?? null : null);

/**
 * D103, D106: the follow-up of a batch of installations (one, for a single application): the whole batch at a glance
 * on the side (progress, counts, the order with what each waits for), what to do first (a failure, its cause and its
 * actions), then each application's seven steps in place (Accordion), and where to go once all are in service.
 */
export function InstallationFollowUp({ server, items, loading, password, extra, onAct, onVariables, onOpen, onLeave, onProject, failure }: {
  server: string;
  items: Installation[];
  loading: boolean;
  /** D100: the access password, shown by the page that started the batch only. */
  password: ReactNode;
  /** What comes after the batch (the rename of the server). */
  extra?: ReactNode;
  onAct: (i: Installation, action: 'resume' | 'abandon') => void;
  onVariables: (i: Installation) => void;
  onOpen: (path: string) => void;
  onLeave: () => void;
  onProject: (project: string) => void;
  failure?: ReactNode;
}) {
  const total = items.length;
  const count = (k: Kind) => items.filter(i => kindOf(i) === k).length;
  const done = count('done');
  const failed = items.filter(i => kindOf(i) === 'failed');
  const running = items.find(i => ['running', 'variables'].includes(kindOf(i)));
  const project = items[0]?.project ?? '';
  const allDone = total > 0 && items.some(i => kindOf(i) === 'done') && items.every(i => ['done', 'abandoned', 'retired'].includes(kindOf(i)));
  const byDeployment = (name: string | null | undefined) => items.find(i => i.deployment === name);
  const exposure = (i: Installation) => <Badge tone="neutral" size="sm">{t(i.domain ? 'install.follow.exposed' : 'install.follow.internal')}</Badge>;

  const describe = (i: Installation) => {
    const k = kindOf(i);
    const c = currentStep(i);
    if (k === 'done') return t('install.follow.inService');
    if (k === 'abandoned') return t('install.follow.abandoned');
    if (k === 'retired') return t('install.follow.retired');
    if (k === 'failed') return t('install.follow.failedAt', { step: t(`install.progress.${i.failedStep ?? c?.step ?? 'reserve'}`) });
    if (k === 'waiting') return byDeployment(i.waitingFor)?.state === 'failed' ? t('install.follow.waitsFailed', { deployment: i.waitingFor ?? '' }) : t('install.follow.waits', { deployment: i.waitingFor ?? '' });
    if (k === 'variables') return t('install.follow.variables');
    return c ? t('install.follow.at', { step: t(`install.progress.${c.step}`), since: c.since ? durationSince(c.since) : '—' }) : '';
  };
  const label = (i: Installation) => (kindOf(i) === 'waiting' ? t('install.follow.waitsShort', { deployment: i.waitingFor ?? '' }) : t(`install.follow.kinds.${kindOf(i)}`));
  const name = (i: Installation) => (total > 1 ? `${items.indexOf(i) + 1}. ${i.deployment}` : i.deployment);

  const lead = !total ? undefined
    : allDone ? t('install.follow.leadDone', { n: done })
    : failed.length ? t('install.follow.leadFailed', { deployment: failed[0]!.deployment })
    : total > 1 ? t('install.follow.leadRunning', { done, total, deployment: running?.deployment ?? '—' })
    : kindOf(items[0]!) === 'abandoned' ? t('install.follow.leadAbandoned') : describe(items[0]!);

  const actionsOf = (i: Installation) => {
    const k = kindOf(i);
    const buttons: ReactNode[] = [];
    if (k === 'variables' || (k === 'failed' && !i.steps.secrets)) buttons.push(<Button key="variables" variant="secondary" size="sm" icon="key" onClick={() => onVariables(i)}>{t('install.follow.variablesAction')}</Button>);
    if (k === 'retired') return [<Button key="open" variant="secondary" size="sm" trailingIcon="arrow_forward" onClick={() => onOpen(`/environments/${encodeURIComponent(i.deployment)}`)}>{t('install.toEnvironment')}</Button>];
    if (i.steps.catalogue && k !== 'abandoned') buttons.push(<Button key="logs" variant="secondary" size="sm" icon="article" onClick={() => onOpen(`/environments/${encodeURIComponent(i.deployment)}?tab=logs`)}>{t('install.follow.logs')}</Button>);
    if (i.failedStep === 'deploy') buttons.push(<Link key="workflow" href={`https://github.com/${i.repository}/actions/workflows/deploy.yml`} external>{t('install.follow.workflow')}</Link>);
    if (['reserved', 'failed'].includes(i.state) && !i.steps.catalogue) buttons.push(<Button key="abandon" variant="secondary" size="sm" icon="close" onClick={() => onAct(i, 'abandon')}>{t('install.abandon')}</Button>);
    if (['deployed', 'in_service'].includes(i.state)) buttons.push(<Button key="open" variant="secondary" size="sm" trailingIcon="arrow_forward" onClick={() => onOpen(`/environments/${encodeURIComponent(i.deployment)}`)}>{t('install.toEnvironment')}</Button>);
    return buttons;
  };
  const detail = (i: Installation) => (
    <Stack gap={12}>
      {i.expired && <Alert tone="warn" title={t('install.expired')}>{t('install.expiredBody')}</Alert>}
      {i.state === 'ready' && i.missingSecrets.length > 0 && (
        <Alert tone="warn" title={t('install.secrets.title', { n: i.missingSecrets.length })} action={t('install.secrets.action')} onAction={() => onVariables(i)}>
          {t('install.secrets.body', { names: i.missingSecrets.join(', ') })}
        </Alert>
      )}
      {i.waitingFor && <Text variant="body-sm" tone="mute">{t('install.batch.waitingBody')}</Text>}
      {resumes(i) > 0 && <Text variant="body-sm" tone="mute">{t('install.follow.resumed', { n: resumes(i) })}</Text>}
      {/* The actions go where they act: on the failed step for a failure, above the steps otherwise. */}
      {kindOf(i) !== 'failed' && actionsOf(i).length > 0 && <Inline gap={8} align="center">{actionsOf(i)}</Inline>}
      {kindOf(i) === 'retired' ? <Text variant="body-sm">{t('install.follow.retiredBody')}</Text>
        : <Timeline label={t('install.progressLabel')} items={progressItems(i, kindOf(i) === 'failed' && actionsOf(i).length > 0 ? <Inline gap={8} align="center">{actionsOf(i)}</Inline> : undefined)} />}
    </Stack>
  );

  return (
    <SplitLayout
      title={t('install.title', { server })}
      onBack={onLeave}
      backLabel={t('install.quit')}
      heading={total ? t('install.follow.heading', { project }) : t('install.done.loading')}
      lead={lead}
      split="2-1"
      asideLabel={t('install.follow.asideLabel')}
      aside={total ? (
        <Stack gap={16}>
          <ProgressBar value={Math.round((done / total) * 100)} size="lg" label={t('install.follow.progress', { done, total })} ariaLabel={t('install.follow.progressLabel')} />
          <DefinitionList
            items={[
              { key: 'done', label: t('install.follow.kinds.done'), value: String(done) },
              { key: 'running', label: t('install.follow.kinds.running'), value: String(count('running') + count('variables')) },
              { key: 'waiting', label: t('install.follow.kinds.waiting'), value: String(count('waiting')) },
              { key: 'failed', label: t('install.follow.kinds.failed'), value: String(failed.length) },
              ...(count('abandoned') ? [{ key: 'abandoned', label: t('install.follow.kinds.abandoned'), value: String(count('abandoned')) }] : []),
            ]}
          />
          {total > 1 && (
            <Stack gap={8}>
              <Text variant="section-title" as="h2">{t('install.follow.order')}</Text>
              <Timeline compact label={t('install.follow.order')} items={items.map(i => ({
                id: i.id, title: name(i), badge: exposure(i), description: describe(i),
                status: ({ done: 'done', failed: 'error', running: 'current', variables: 'current', waiting: 'upcoming', abandoned: 'upcoming', retired: 'upcoming' } as const)[kindOf(i)],
                when: i.steps.service ? relativeTime(i.steps.service) : undefined,
              }))} />
            </Stack>
          )}
          {items.some(i => i.domain) && (
            <Stack gap={8}>
              <Text variant="section-title" as="h2">{t('install.follow.access')}</Text>
              {items.filter(i => i.domain).map(i => (
                <Stack key={i.id} gap={4}>
                  <Text variant="code">{i.domain}</Text>
                  <Text variant="body-sm" tone="mute">{i.access.protected ? t('install.follow.protected', { user: i.access.user ?? '' }) : t('access.public')}</Text>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      ) : undefined}
      footerStart={<Button variant="secondary" icon="arrow_back" onClick={onLeave}>{t('install.toServer')}</Button>}
      footerEnd={allDone && project ? <Button trailingIcon="arrow_forward" onClick={() => onProject(project)}>{t('install.follow.toProject')}</Button> : undefined}
      footerText=""
    >
      <Stack gap={24}>
        {failure}
        {password}
        {loading && !total && <Text tone="mute">{t('install.done.loading')}</Text>}
        {failed.map(i => (
          <Alert key={i.id} tone="neg" title={t('install.follow.failedTitle', { deployment: i.deployment, step: t(`install.progress.${i.failedStep ?? 'reserve'}`) })}
            action={t('install.resume')} onAction={() => onAct(i, 'resume')}>
            {[
              i.failure,
              lastResume(i) ? t('install.follow.failedAgain', { when: relativeTime(lastResume(i)!) }) : null,
              items.some(x => x.waitingFor === i.deployment) ? t('install.follow.suspended', { deployment: i.deployment }) : null,
            ].filter(Boolean).join(' ')}
          </Alert>
        ))}
        {allDone && (
          <Stack gap={12}>
            <Alert tone="pos" title={t('install.follow.allDone', { n: done })} />
            <DefinitionList items={items.filter(i => kindOf(i) === 'done').map(i => ({
              key: i.id, label: i.deployment,
              value: <Inline gap={12} align="center"><Link href={`/environments/${encodeURIComponent(i.deployment)}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); onOpen(`/environments/${encodeURIComponent(i.deployment)}`); }}>{t('install.toEnvironment')}</Link>{i.domain && <Text variant="code">{i.domain}</Text>}</Inline>,
            }))} />
            {extra}
          </Stack>
        )}
        {total === 1 && detail(items[0]!)}
        {total > 1 && (
          <Accordion
            multiple
            toggleAll
            headingLevel={2}
            defaultOpen={[(failed[0] ?? running ?? items.find(i => kindOf(i) === 'waiting'))?.id].filter((x): x is string => Boolean(x))}
            items={items.map(i => ({
              id: i.id,
              title: name(i),
              subtitle: i.domain || t('install.follow.internal'),
              meta: <Badge tone={TONES[kindOf(i)]} size="sm">{label(i)}</Badge>,
              content: detail(i),
            }))}
          />
        )}
      </Stack>
    </SplitLayout>
  );
}
